import React from 'react';
import { useSettings } from '../contexts/SettingsContext';
import { useTheme } from '../contexts/ThemeContext';
import '../styles/chat.css';

/**
 * Reusable ChatCard component for consistent card styling across chat pages
 * 
 * @param {Object} props
 * @param {React.ReactNode} props.children - Card content
 * @param {string} props.variant - Card variant: 'primary' | 'secondary'
 * @param {string} props.size - Padding size: 'sm' | 'md' | 'lg'
 * @param {string} props.rounded - Border radius: 'lg' | 'xl'
 * @param {React.ReactNode} props.title - Optional card title
 * @param {string} props.titleSize - Title size: 'sm' | 'md' | 'lg'
 * @param {React.ReactNode} props.footer - Optional footer content
 * @param {string} props.className - Additional CSS classes
 * @param {Object} props.style - Additional inline styles
 */
const ChatCard = ({
  children,
  variant = 'primary',
  size = 'md',
  rounded = 'xl',
  title,
  titleSize = 'md',
  footer,
  className = '',
  style = {},
}) => {
  const { settings } = useSettings();
  const { isDark } = useTheme();
  const primaryColor = settings.theme?.colors?.primary || '#3B5BDB';
  const secondaryColor = settings.theme?.colors?.secondary || '#20C997';

  // Convert hex to rgba helper
  const hexToRgba = (hex, alpha) => {
    const r = parseInt(hex.slice(1, 3), 16);
    const g = parseInt(hex.slice(3, 5), 16);
    const b = parseInt(hex.slice(5, 7), 16);
    return `rgba(${r}, ${g}, ${b}, ${alpha})`;
  };

  // Build base classes
  const baseClasses = ['chat_card', `chat_card_${variant}`];
  
  // Add size class
  if (size) {
    baseClasses.push(`chat_card_padding_${size}`);
  }
  
  // Add rounded class
  if (rounded) {
    baseClasses.push(`chat_card_rounded_${rounded}`);
  }

  // Build dynamic styles based on variant and theme
  // Note: We use inline styles here because colors come from settings context
  // The CSS classes provide base structure, but colors are dynamic
  const dynamicStyles = {
    ...style,
  };

  if (variant === 'primary') {
    if (isDark) {
      dynamicStyles.borderColor = hexToRgba(primaryColor, 0.51);
      dynamicStyles.backgroundColor = 'rgba(15, 23, 42, 0.9)';
    } else {
      dynamicStyles.borderColor = hexToRgba(primaryColor, 0.2);
      dynamicStyles.backgroundColor = hexToRgba(primaryColor, 0.1);
    }
  } else if (variant === 'secondary') {
    if (isDark) {
      dynamicStyles.borderColor = hexToRgba(secondaryColor, 0.33);
      dynamicStyles.backgroundColor = 'rgba(15, 23, 42, 0.9)';
    } else {
      dynamicStyles.borderColor = hexToRgba(secondaryColor, 0.2);
      dynamicStyles.backgroundColor = hexToRgba(secondaryColor, 0.1);
    }
  }

  // Title size mapping
  const titleSizeClass = {
    sm: 'chat_heading_sm',
    md: 'chat_heading_md',
    lg: 'chat_heading_lg',
  }[titleSize] || 'chat_heading_md';

  return (
    <div
      className={`${baseClasses.join(' ')} ${className}`}
      style={dynamicStyles}
    >
      {title && (
        <div className={`${titleSizeClass} chat_mb_md`}>
          {title}
        </div>
      )}
      <div className="chat_flex_1">
        {children}
      </div>
      {footer && (
        <div className="chat_mt_md">
          {footer}
        </div>
      )}
    </div>
  );
};

export default ChatCard;

